import React, { useEffect, useMemo, useRef } from 'react';
import {useParams} from 'react-router-dom'
import { JsonViewer } from '../../../../../../../common/JsonViewer.jsx';
import { HeaderTitle } from '../../../../../../../common/HeaderTitle.jsx';
import {
    Badge,
    Alert,
    Card,
    Accordion,
    useAccordionButton
} from "react-bootstrap";
import { toCurrency } from "../../../../../../../utils/helpers/index.js";
import ButtonWithIcon from "../../../../../../../common/ButtonWithIcon.jsx";
import {Icons} from "../../../../../../../common/Icons.jsx";
import {usePlanHarmData} from '../../../../../../../../api/domain/elementyRejstru/queries/usePlanHarmData.jsx'
import { TaskItemCard } from '../parts/TaskItemCard.jsx'
import { ProjectSummaryCard } from '../parts/ProjectSummaryCard.jsx'
import {
    PHASES,
    determineStatus,
    processPhasesData,
    calculateSummary,
} from '../parts/harmonogramHelpers.js'

const CustomToggle = ({ children, eventKey, variant }) => {
    const decoratedOnClick = useAccordionButton(eventKey);
    return (
        <ButtonWithIcon
            variant={variant || 'light'}
            onClick={decoratedOnClick}
            icon={<Icons.details />}
            text={children}
            size="sm"
            className="ms-3"
        />
    );
};

const RealizacjaForm = ({ wiz }) => {
    const updateListItem = (listKey, index, newRow) => {
        const f = { ...(wiz?.form || {}) };
        f[listKey] = Array.isArray(f[listKey]) ? [...f[listKey]] : [];
        f[listKey][index] = newRow;
        if (typeof wiz?.setWizForm === 'function') {
            wiz.setWizForm(f);
        } else if (typeof wiz?.setValue === 'function') {
            wiz.setValue(listKey, f[listKey]);
        }
    };

    const phasesData = useMemo(() => processPhasesData(wiz?.form || {}), [wiz?.form]);

    const globalSummary = useMemo(() => calculateSummary(phasesData), [phasesData]);

    if (phasesData.every(p => p.tasks.length === 0)) {
        return <Alert variant="info" className="mt-3">Brak zadań w harmonogramie.</Alert>;
    }

    const defaultOpen = [];

    return (
        <div className={'d-flex flex-column gap-3'}>
            <Accordion defaultActiveKey={defaultOpen} alwaysOpen>
                {phasesData.map(phase => {
                    if (phase.tasks.length === 0) return null;

                    return (
                        <Card key={phase.key} className={`mb-3 border-${phase.status.variant}`} style={{ borderLeftWidth: '6px' }}>
                            <Card.Header className="bg-light p-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div className="d-flex align-items-center">
                                        <div>
                                            <h5 className="mb-0 fw-bold text-uppercase">{phase.name}</h5>
                                            <small>Liczba pozycji: {phase.tasks.length}</small>
                                        </div>
                                    </div>
                                    <div>
                                        <Badge bg={phase.status.variant} className="me-3 p-2 fs-6">
                                            {phase.status.label}
                                        </Badge>
                                    </div>
                                    <div className="d-flex align-items-center gap-4 ms-auto">
                                        <div className="text-end d-none d-md-block">
                                            <small className="d-block">Budżet Etapu [ZŁ]</small>
                                            <strong>{toCurrency(phase.totalPlan)}</strong>
                                        </div>
                                        <div className="text-end d-none d-md-block">
                                            <small className="d-block">% Wydatkowania</small>
                                            <strong className={phase.percent > 100 ? 'text-danger' : 'text-success'}>
                                                {phase.percent.toFixed(1)}%
                                            </strong>
                                        </div>


                                        <CustomToggle eventKey={phase.key} variant={`outline-${phase.status.variant}`}>
                                            Szczegóły
                                        </CustomToggle>
                                    </div>
                                </div>
                            </Card.Header>
                            <Accordion.Collapse eventKey={phase.key}>
                                <Card.Body>
                                    {phase.tasks.map((task) => {
                                        const row = (wiz?.form || {})[phase.listKey]?.[task.index] || {};
                                        const setRow = (key, value) => {
                                            const formData = wiz?.form || {};
                                            const currentList = Array.isArray(formData[phase.listKey])
                                                ? formData[phase.listKey]
                                                : [];
                                            const currentRow = currentList[task.index] || {};
                                            if (currentRow?.[key] === value) {
                                                return;
                                            }
                                            const newRow = { ...currentRow, [key]: value };
                                            updateListItem(phase.listKey, task.index, newRow);
                                        };

                                        return (
                                            <TaskItemCard
                                                key={task.id}
                                                task={task}
                                                row={row}
                                                setRow={setRow}
                                                idx={task.index}
                                                wiz={wiz}
                                            />
                                        );
                                    })}
                                </Card.Body>
                            </Accordion.Collapse>
                        </Card>
                    );
                })}
            </Accordion>

            <ProjectSummaryCard summary={globalSummary} />
        </div>
    );
};

const HarmonogramRealizacji = (props) => {
    const {rejestrId, planHarmId} = useParams()
    const prefilledRef = useRef(false)

    const {data: planHarmData} = usePlanHarmData(rejestrId, planHarmId, !!rejestrId)

    useEffect(() => {
        if (prefilledRef.current) return
        if (!props.wiz) return

        const current = props.wiz.form || {}
        const keys = [
            'inicjacja_zadania_list',
            'przygotowania_zadania_list',
            'realizacja_zadania_list',
            'zamykania_zadania_list',
        ]

        const currentHasAny = keys.some((k) => Array.isArray(current[k]) && current[k].length > 0)
        if (!currentHasAny && !planHarmData) return

        const normalizeTask = (t) => {
            const wydatkowanie =
                t?.realizacja_wydatkowanie_narastajaco ?? t?.wydatkowanie_narastajaco ?? '0'
            const plan = parseFloat(t?.budzet_planowany || 0)
            const wydatkowanieValue = parseFloat(wydatkowanie || 0)
            const percent = plan > 0 ? (wydatkowanieValue / plan) * 100 : 0
            const computedPercent = Number.isFinite(percent) ? percent.toFixed(2) : ''
            const hasPercent = ![
                null,
                undefined,
                '',
            ].includes(t?.realizacja_procent_wydatkowania)

            return {
                ...t,
                realizacja_wydatkowanie_narastajaco: wydatkowanie,
                realizacja_ocena: t?.realizacja_ocena ?? t?.ocena ?? 'Nie rozpoczęty',
                realizacja_procent_wydatkowania: hasPercent
                    ? t?.realizacja_procent_wydatkowania
                    : computedPercent,
            }
        }

        const getSourceList = (key) => {
            if (currentHasAny) {
                return Array.isArray(current[key]) ? current[key] : []
            }
            return Array.isArray(planHarmData?.[key]) ? planHarmData[key] : []
        }

        const nextForm = {
            ...current,
            inicjacja_zadania_list: getSourceList('inicjacja_zadania_list').map(normalizeTask),
            przygotowania_zadania_list: getSourceList('przygotowania_zadania_list').map(normalizeTask),
            realizacja_zadania_list: getSourceList('realizacja_zadania_list').map(normalizeTask),
            zamykania_zadania_list: getSourceList('zamykania_zadania_list').map(normalizeTask),
        }

        const hasChanges = keys.some((key) => {
            const nextList = nextForm[key]
            const currentList = Array.isArray(current[key]) ? current[key] : []
            return JSON.stringify(nextList) !== JSON.stringify(currentList)
        })

        if (hasChanges) {
            props.wiz.setWizForm(nextForm)
        }
        prefilledRef.current = true
    }, [props.wiz, planHarmData])

    return (
        <>
            <JsonViewer src={props.wiz?.form || {}} show={false}/>
            <HeaderTitle noHr size={'fs-3'} title={'B. Harmonogram Realizacji'}/>
            <HeaderTitle
                noHr
                size={'fs-4'}
                title={'Monitorowanie wykonania budżetu projektu od początku jego realizacji (wydatkowanie narastająco)'}
            />
            <RealizacjaForm wiz={props.wiz} />
        </>
    )
}

export default HarmonogramRealizacji
